import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X, Coins, Gift, LogOut, Sparkles, User as UserIcon } from 'lucide-react';
import { Button } from './ui/button';
import BuyCreditsModal from './BuyCreditsModal';
import EarnCreditsModal from './EarnCreditsModal';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LINKS = [
  { label: 'Features', href: '/#features', section: true },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Showcase', href: '/showcase' },
  { label: 'Community', href: '/showcase/community' },
  { label: 'FAQs', href: '/#faqs', section: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [earnOpen, setEarnOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, login, logout, loading } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const initials = (user?.name || user?.email || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/75 backdrop-blur-xl border-b border-neutral-200/70' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0" data-testid="brand-link">
          <img
            src="/logo.png"
            alt="Transformerus"
            className="w-9 h-9 object-contain transition-transform group-hover:scale-105"
            data-testid="brand-logo"
          />
          <span className="tfu-logo text-[20px] text-neutral-900 tracking-tight">transformerus</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 bg-white/60 backdrop-blur-md border border-neutral-200/60 rounded-full px-2 py-1.5">
          {LINKS.map((link) => (
            link.section ? (
              <a key={link.label} href={link.href}
                className="px-3.5 py-1.5 text-sm font-medium text-neutral-700 hover:text-neutral-900 rounded-full hover:bg-neutral-100 transition-colors">
                {link.label}
              </a>
            ) : (
              <Link key={link.label} to={link.href}
                className={`px-3.5 py-1.5 text-sm font-medium rounded-full transition-colors ${
                  pathname === link.href ? 'bg-neutral-900 text-white' : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100'
                }`}>
                {link.label}
              </Link>
            )
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <button onClick={() => setEarnOpen(true)}
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full text-sm font-medium bg-gradient-to-r from-emerald-50 to-cyan-50 hover:from-emerald-100 hover:to-cyan-100 border border-emerald-200/60 text-emerald-800 transition-colors">
            <Gift className="w-4 h-4" /> Earn $200
          </button>
          <button onClick={() => setCreditsOpen(true)}
            className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full text-sm font-medium bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-800 transition-colors">
            <Coins className="w-4 h-4 text-amber-500" /> Buy Credits
          </button>

          {!loading && (
            user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 h-10 pl-1 pr-3 rounded-full bg-white border border-neutral-200 hover:bg-neutral-50 transition-colors">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-300 to-indigo-400 text-white text-xs font-semibold flex items-center justify-center">
                        {initials}
                      </div>
                    )}
                    <span className="text-sm font-medium text-neutral-800 max-w-[120px] truncate">{user.name?.split(' ')[0] || user.email}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs">
                    <div className="font-semibold text-neutral-900">{user.name}</div>
                    <div className="text-neutral-500 truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/build" className="cursor-pointer">
                      <Sparkles className="w-4 h-4 mr-2" /> Builder
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/referrals" className="cursor-pointer">
                      <Gift className="w-4 h-4 mr-2" /> My referrals
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setEarnOpen(true)} className="cursor-pointer">
                    <Gift className="w-4 h-4 mr-2" /> Refer & earn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setCreditsOpen(true)} className="cursor-pointer">
                    <Coins className="w-4 h-4 mr-2 text-amber-500" /> Buy credits
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-rose-600 focus:text-rose-700">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button onClick={login}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-medium bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-800 transition-colors">
                <UserIcon className="w-4 h-4" /> Sign in
              </button>
            )
          )}

          <Link to="/build">
            <Button className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white h-10 px-4 group">
              Get Started
              <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          </Link>
        </div>

        <button className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-neutral-100"
          onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-b border-neutral-200 px-6 py-4 space-y-1">
          {LINKS.map((link) =>
            link.section ? (
              <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium text-neutral-700">{link.label}</a>
            ) : (
              <Link key={link.label} to={link.href} onClick={() => setMobileOpen(false)} className="block py-2 text-sm font-medium text-neutral-700">{link.label}</Link>
            )
          )}
          <div className="grid grid-cols-2 gap-2 pt-3">
            <button onClick={() => { setMobileOpen(false); setEarnOpen(true); }}
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-medium bg-emerald-50 border border-emerald-200 text-emerald-800">
              <Gift className="w-4 h-4" /> Earn $200
            </button>
            <button onClick={() => { setMobileOpen(false); setCreditsOpen(true); }}
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-medium bg-white border border-neutral-200 text-neutral-800">
              <Coins className="w-4 h-4 text-amber-500" /> Buy Credits
            </button>
          </div>
          {!loading && (
            user ? (
              <Button onClick={() => { setMobileOpen(false); logout(); }} variant="outline" className="mt-2 w-full rounded-full">
                <LogOut className="w-4 h-4 mr-2" /> Sign out ({user.email})
              </Button>
            ) : (
              <Button onClick={() => { setMobileOpen(false); login(); }} variant="outline" className="mt-2 w-full rounded-full">
                <UserIcon className="w-4 h-4 mr-2" /> Sign in
              </Button>
            )
          )}
          <Link to="/build" onClick={() => setMobileOpen(false)}>
            <Button className="mt-2 w-full rounded-full bg-neutral-900 text-white">Get Started</Button>
          </Link>
        </div>
      )}

      <BuyCreditsModal open={creditsOpen} onOpenChange={setCreditsOpen} />
      <EarnCreditsModal open={earnOpen} onOpenChange={setEarnOpen} />
    </header>
  );
}
